import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QuickNoteModal = ({ date, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    note: '',
    priority: 'medium',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);

  const noteCategories = [
    { value: 'general', label: 'General Note', icon: 'StickyNote', color: 'text-gray-500' },
    { value: 'meeting', label: 'Meeting Note', icon: 'Users', color: 'text-blue-500' },
    { value: 'task', label: 'Task Reminder', icon: 'CheckSquare', color: 'text-green-500' },
    { value: 'idea', label: 'Idea/Brainstorm', icon: 'Lightbulb', color: 'text-yellow-500' },
    { value: 'followup', label: 'Follow-up', icon: 'ArrowRight', color: 'text-purple-500' },
    { value: 'important', label: 'Important', icon: 'AlertCircle', color: 'text-red-500' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.note?.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave?.({
        title: formData?.title || `${noteCategories?.find(cat => cat?.value === formData?.category)?.label}`,
        note: formData?.note,
        date: date,
        priority: formData?.priority,
        category: formData?.category
      });
    } catch (err) {
      console.error('Failed to save quick note:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = noteCategories?.find(cat => cat?.value === formData?.category) || noteCategories?.[0];

  return (
    <div className="fixed inset-0 z-50 bg-background/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="StickyNote" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Quick Note</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            iconName="X"
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Date & Time
            </label>
            <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">
                {format(date || new Date(), 'EEEE, MMMM d, yyyy')} at {format(new Date(), 'HH:mm')}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Category
            </label>
            <div className="grid grid-cols-3 gap-2">
              {noteCategories?.map(category => (
                <button
                  key={category?.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: category?.value }))}
                  className={`
                    p-3 rounded-lg border transition-all text-center
                    ${formData?.category === category?.value 
                      ? 'border-primary bg-primary/10' :'border-border hover:border-muted-foreground hover:bg-muted/30'
                    }
                  `}
                >
                  <Icon 
                    name={category?.icon} 
                    size={16} 
                    className={`mx-auto mb-1 ${
                      formData?.category === category?.value ? 'text-primary' : category?.color
                    }`} 
                  />
                  <span className="text-xs text-foreground">{category?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title (Optional)
            </label>
            <Input
              type="text"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              placeholder={`${selectedCategory?.label} - ${format(date || new Date(), 'MMM d')}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Note Content *
            </label>
            <textarea
              value={formData?.note}
              onChange={(e) => setFormData(prev => ({ ...prev, note: e?.target?.value }))}
              placeholder="Write your note here... (e.g., meeting outcomes, task reminders, important information, ideas, etc.)"
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-vertical"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be saved as an event note in your calendar
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Priority
            </label>
            <select
              value={formData?.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading || !formData?.note?.trim()}
              iconName={loading ? "Loader2" : "Save"}
              iconPosition="left"
            >
              {loading ? 'Saving...' : 'Save Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickNoteModal;